import React, { useState, useEffect } from 'react';
import { stripeService } from '../services/stripe';
import { getCurrentUserId } from '../services/database';
import proxyapi from '../services/ProxyApi';

const PaymentProcessing = ({ selectedPlan, onCancel }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  
  // Check if user has an active subscription on component mount
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const userId = await getCurrentUserId();
        if (!userId) return;
        
        const response = await proxyapi.get(`/api/v1/users/${userId}/subscription`);
        setSubscription(response.data);
        
        // If user already has an active subscription, show error
        if (response.data && response.data.status === 'active') {
          setError(`You already have an active subscription that expires on ${formatDate(response.data.expiresAt)}.`);
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
      }
    };
    
    checkSubscription();
  }, []);
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  const handleProcessPayment = async () => {
    if (!selectedPlan) return;
    
    // Double-check that user doesn't have an active subscription
    if (subscription && subscription.status === 'active') {
      setError(`You already have an active subscription that expires on ${formatDate(subscription.expiresAt)}.`);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const userId = await getCurrentUserId();
      
      if (!userId) {
        throw new Error('User ID is required. Please make sure you are logged in.');
      }
      
      const planData = {
        userId,
        price: selectedPlan.price,
        period: selectedPlan.period,
        title: selectedPlan.title || 'Pay as You Go'
      };
      
      // This will redirect to Stripe Checkout
      await stripeService.createCheckoutSession(planData);
      
    } catch (error) {
      console.error('Checkout error:', error);
      setError(error.message || 'Failed to create checkout session');
      setIsLoading(false);
    }
  };

  return (
    <div className="payment-container max-w-md mx-auto p-4">
      {/* Package Details */}
      {selectedPlan && (
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <h2 className="text-xl font-semibold mb-2">{selectedPlan.title || 'Pay as You Go'}</h2>
          <div className="text-center mb-4">
            <p className="text-3xl font-bold text-gray-900">${selectedPlan.price}</p>
            <p className="text-gray-600">for {selectedPlan.period} days</p>
          </div>
        </div>
      )}

      {/* Active Subscription Warning */}
      {subscription && subscription.status === 'active' && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative mb-4">
          <strong className="font-bold">Note: </strong>
          <span className="block sm:inline">
            You already have an active subscription that expires on {formatDate(subscription.expiresAt)}.
            You can purchase a new plan after your current subscription expires.
          </span>
        </div>
      )}

      {/* Error Message */}
      {error && !(subscription?.status === 'active') && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {/* Payment Button */}
      <button
        onClick={handleProcessPayment}
        disabled={isLoading || (subscription && subscription.status === 'active')}
        className={`w-full py-3 rounded mb-4 ${
          isLoading || (subscription && subscription.status === 'active') 
            ? 'bg-gray-400 cursor-not-allowed' 
            : 'bg-black hover:bg-gray-800'
        } text-white font-medium`}
      >
        {isLoading ? 'Processing...' : 'Proceed to Payment'}
      </button>

      {/* Cancel Button */}
      <div className="text-center">
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="text-gray-600 hover:text-black"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default PaymentProcessing;